import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { FaTrash, FaPlus, FaBars } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const MAX_CATEGORIES = 10;
const MAX_CATEGORY_LENGTH = 15;

const ManageCategories = () => {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userRef = doc(db, "users", currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists() && userDoc.data().categories) {
          setCategories(userDoc.data().categories);
        } else {
          // Initialize with defaults if not present
          setCategories(["Home", "Work"]);
        }
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate, auth, db]);

  const handleAddCategory = async () => {
    setError("");
    const trimmedCategory = newCategory.trim();

    if (!trimmedCategory) {
      setError("Category name cannot be empty.");
      return;
    }
    if (trimmedCategory.length > MAX_CATEGORY_LENGTH) {
      setError(
        `Category name cannot exceed ${MAX_CATEGORY_LENGTH} characters.`,
      );
      return;
    }
    if (categories.length >= MAX_CATEGORIES) {
      setError(`You can have a maximum of ${MAX_CATEGORIES} categories.`);
      return;
    }
    if (
      categories.find((c) => c.toLowerCase() === trimmedCategory.toLowerCase())
    ) {
      setError("This category already exists.");
      return;
    }

    const updatedCategories = [...categories, trimmedCategory];
    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { categories: updatedCategories });
      setCategories(updatedCategories);
      setNewCategory("");
    } catch (e) {
      console.error("Error adding category:", e);
      setError("Could not add category. Please try again.");
    }
  };

  const handleDeleteCategory = async (categoryToDelete) => {
    if (!user) return;

    const updatedCategories = categories.filter((c) => c !== categoryToDelete);

    try {
      const batch = writeBatch(db);

      // 1. Update the user's category list
      const userRef = doc(db, "users", user.uid);
      batch.update(userRef, { categories: updatedCategories });

      // 2. Find all addresses with the deleted category and un-assign them
      const addressesRef = collection(db, "users", user.uid, "addresses");
      const q = query(addressesRef, where("category", "==", categoryToDelete));
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((addressDoc) => {
        batch.update(addressDoc.ref, { category: "" });
      });

      // 3. Commit all changes at once
      await batch.commit();

      // 4. Update local state
      setCategories(updatedCategories);
    } catch (e) {
      console.error("Error deleting category:", e);
      setError(
        "Could not delete category and update addresses. Please try again.",
      );
    }
  };

  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }

    const reorderedCategories = Array.from(categories);
    const [removed] = reorderedCategories.splice(source.index, 1);
    reorderedCategories.splice(destination.index, 0, removed);

    setCategories(reorderedCategories);

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, { categories: reorderedCategories });
    } catch (error) {
      console.error("Error updating category order:", error);
      // Revert state on error
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      if (userDoc.exists() && userDoc.data().categories) {
        setCategories(userDoc.data().categories);
      }
    }
  };

  return (
    <div
      className="container"
      style={{
        width: "80%",
        maxWidth: "400px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2>Manage Categories</h2>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="categories-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ marginBottom: "20px" }}
            >
              {categories.map((category, index) => (
                <Draggable key={category} draggableId={category} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "8px",
                        borderBottom: "1px solid var(--border-color, #eee)",
                        backgroundColor: "var(--card-bg, #fff)",
                      }}
                    >
                      <div
                        {...provided.dragHandleProps}
                        style={{
                          cursor: "grab",
                          marginRight: "10px",
                          color: "var(--text-secondary, #aaa)",
                        }}
                      >
                        <FaBars />
                      </div>
                      <span style={{ flex: 1 }}>{category}</span>
                      <button
                        onClick={() => handleDeleteCategory(category)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {" "}
                        <FaTrash color="#DC3545" />{" "}
                      </button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {categories.length < MAX_CATEGORIES && (
        <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category name"
            maxLength={MAX_CATEGORY_LENGTH}
            style={{
              flex: 1,
              padding: "10px 15px",
              borderRadius: "20px",
              border: "1px solid var(--border-color, #ccc)",
            }}
          />
          <button
            onClick={handleAddCategory}
            style={{
              padding: "10px",
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              borderRadius: "5px",
            }}
          >
            <FaPlus />
          </button>
        </div>
      )}

      {error && <p style={{ color: "red", fontSize: "13px" }}>{error}</p>}

      <button
        onClick={() => navigate("/")}
        style={{
          marginTop: "20px",
          backgroundColor: "gray",
          color: "white",
          padding: "10px 15px",
          border: "none",
          cursor: "pointer",
          borderRadius: "5px",
        }}
      >
        Back to List
      </button>
    </div>
  );
};

export default ManageCategories;
